import { Fragment, type ReactNode } from 'react';

import classNames from 'classnames';

import { Button, Flex } from '@spotify-internal/encore-web';
import {
  AuthScreen,
  useFormFieldsContext,
} from '@spotify/confidence-core-react';

import { type SurveyFormValues, isRoleAnswered } from './surveyForm';

import styles from './OnboardingSurvey.module.css';

type SurveyActionsProps = {
  isSaving: boolean;
  backAction?: ReactNode;
  submitLabel?: string;
  skipLabel?: string;
  loadingAriaLabel?: string;
  canSubmit?: boolean;
  onSkip: () => void;
};

export const SurveyActions = ({
  isSaving,
  backAction,
  onSkip,
  submitLabel = 'Submit',
  skipLabel = 'Skip',
  loadingAriaLabel = 'Saving',
  canSubmit,
}: SurveyActionsProps) => {
  const { values } = useFormFieldsContext<SurveyFormValues>();
  const isSubmittable = canSubmit ?? isRoleAnswered(values);
  const forwardActions = (
    <>
      <Button
        type="submit"
        variant="primary"
        colorSet="inverted"
        size="medium"
        isLoading={isSaving}
        loadingAriaLabel={loadingAriaLabel}
        disabled={!isSubmittable || isSaving}
       data-track-source-anchor="a69b4c542d;survey-actions-button;high">
        {submitLabel}
      </Button>
      <Button
        type="button"
        variant="tertiary"
        size="medium"
        disabled={isSaving}
        onClick={onSkip}
       data-track-source-anchor="d2c68ecb6c;survey-actions-button-2">
        {skipLabel}
      </Button>
    </>
  );

  if (!backAction) {
    return (
      <AuthScreen.Actions
        justifyContent="end"
        className={classNames(styles.forwardOnlyActions, styles.forwardActions)}
      >
        {forwardActions}
      </AuthScreen.Actions>
    );
  }

  return (
    <AuthScreen.Actions
      justifyContent="space-between"
      className={styles.actions}
    >
      <Fragment key="back">{backAction}</Fragment>
      <Flex
        key="forward"
        gap="tighter"
        alignItems="center"
        wrap="wrap"
        className={styles.forwardActions}
      >
        {forwardActions}
      </Flex>
    </AuthScreen.Actions>
  );
};
